import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

export default function CommandCenter() {
  const [metrics, setMetrics] = useState(null);
  const [byLocation, setByLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [site, setSite] = useState("");
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [drill, setDrill] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/locations").then((r) => (r.ok ? r.json() : [])).then(setLocations);
    apiFetch("/me/preferences").then((r) => (r.ok ? r.json() : { preferences: {} }))
      .then((d) => { setSite(d.preferences?.ecc_location_key || ""); setReady(true); });
  }, [navigate]);

  const load = useCallback((locationKey) => {
    const qs = locationKey ? `?location_key=${encodeURIComponent(locationKey)}` : "";
    apiFetch(`/ecc/metrics${qs}`).then(async (r) => {
      if (r.status === 403) { setError("You do not have reporting access (reports.read)."); return; }
      if (!r.ok) { setError("Unable to load executive metrics."); return; }
      setMetrics(await r.json());
    });
    apiFetch(`/ecc/by-location${qs}`).then((r) => (r.ok ? r.json() : null)).then(setByLocation);
    setDrill(null);
  }, []);

  useEffect(() => { if (ready) load(site); }, [ready, site, load]);

  const changeSite = (value) => {
    setSite(value);
    apiFetch("/me/preferences", {
      method: "PUT",
      body: JSON.stringify({ preferences: { ecc_location_key: value || null } }),
    });
  };

  const openDrilldown = (key) => {
    const qs = site ? `?location_key=${encodeURIComponent(site)}` : "";
    apiFetch(`/ecc/metrics/${key}/drilldown${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setDrill);
  };

  const siteName = locations.find((l) => l.key === site)?.name;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="ecc-title">Executive Command Center</h1>
          <p className="text-sm text-muted-foreground">
            Every metric shows its definition, owner, and as-of time; drill down to authoritative records.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="ecc-error">{error}</p>}

        <section className="space-y-1" data-testid="ecc-site-filter">
          <label className="text-sm font-medium" htmlFor="ecc-site-select">Site</label>
          <select id="ecc-site-select" data-testid="ecc-site-select"
                  className="block w-full max-w-sm border rounded-md px-3 py-2 text-sm bg-background"
                  value={site} onChange={(e) => changeSite(e.target.value)}>
            <option value="">All sites</option>
            {locations.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
          </select>
          <p className="text-sm text-muted-foreground" data-testid="ecc-site-filter-note">
            {site
              ? `Showing ${siteName}. Metrics whose source does not record a site stay organisation-wide and say so.`
              : "Showing every site. Your choice is remembered the next time you sign in."}
          </p>
        </section>

        {metrics && (
          <section className="grid gap-3 md:grid-cols-2" data-testid="ecc-metrics">
            {metrics.map((m) => (
              <Card key={m.key} data-testid={`metric-card-${m.key}`}
                    className="cursor-pointer hover:border-foreground/40 transition-colors"
                    onClick={() => openDrilldown(m.key)}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{m.name}</CardTitle>
                  <CardDescription>{m.definition}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-semibold" data-testid={`metric-value-${m.key}`}>
                    {m.value}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Owner: {m.owner} · As of {new Date(m.as_of).toLocaleString()}
                  </p>
                  {site && (
                    <p className="text-xs mt-1" data-testid={`metric-site-note-${m.key}`}>
                      {m.site_filter_applied
                        ? `Filtered to ${siteName}`
                        : m.site_filter_note}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
            {metrics.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="ecc-empty">
                No published metrics yet.
              </p>
            )}
          </section>
        )}

        {byLocation && (
          <section className="space-y-3" data-testid="ecc-by-location">
            <h2 className="text-lg font-medium">Demand and SLA attainment by site</h2>
            <p className="text-sm text-muted-foreground" data-testid="by-location-method">
              {byLocation.definition}
            </p>
            {byLocation.row_secured && (
              <p className="text-sm text-amber-800" data-testid="by-location-row-secured">
                {byLocation.withheld_reason}
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-md">
                <caption className="sr-only">
                  Work request demand and SLA attainment grouped by site
                </caption>
                <thead>
                  <tr className="text-left border-b">
                    <th scope="col" className="px-3 py-2">Site</th>
                    <th scope="col" className="px-3 py-2">Total</th>
                    <th scope="col" className="px-3 py-2">Last 30 days</th>
                    <th scope="col" className="px-3 py-2">Open</th>
                    <th scope="col" className="px-3 py-2">Past target</th>
                    <th scope="col" className="px-3 py-2">SLA attainment</th>
                  </tr>
                </thead>
                <tbody>
                  {byLocation.sites.map((s) => (
                    <tr key={s.location_key || "unassigned"} className="border-b last:border-0"
                        data-testid={`by-location-row-${s.location_key || "unassigned"}`}>
                      <th scope="row" className="px-3 py-2 font-medium text-left">
                        {s.location_name}
                      </th>
                      <td className="px-3 py-2">{s.total}</td>
                      <td className="px-3 py-2">{s.last_30_days}</td>
                      <td className="px-3 py-2">{s.open}</td>
                      <td className="px-3 py-2">{s.open_past_target}</td>
                      <td className="px-3 py-2">
                        {s.sla_attainment_pct === null ? "—" : `${s.sla_attainment_pct}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              As of {new Date(byLocation.as_of).toLocaleString()} ·{" "}
              {byLocation.records_scanned} records scanned · owner {byLocation.owner}
            </p>
          </section>
        )}

        {drill && (
          <section data-testid="drilldown-panel" className="space-y-3">
            <h2 className="text-lg font-medium">
              Drill-down: {drill.metric_key} ({drill.row_count} records)
            </h2>
            {drill.site_filter_applied && (
              <p className="text-sm text-muted-foreground" data-testid="drilldown-site-note">
                Narrowed to {siteName}.
              </p>
            )}
            {drill.withheld_count > 0 && (
              <p className="text-sm text-amber-800" data-testid="withheld-notice">
                {drill.withheld_count} records withheld — {drill.withheld_reason}
              </p>
            )}
            <div className="space-y-2">
              {drill.rows.map((row) => (
                <div key={row.id} data-testid={`drilldown-row-${row.id}`}
                     className="border rounded-md px-4 py-2 text-sm font-mono">
                  {Object.entries(row).filter(([k]) => k !== "id")
                    .map(([k, v]) => `${k}=${v}`).join(" · ")}
                </div>
              ))}
              {drill.rows.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="drilldown-empty">
                  No records visible to you.
                </p>
              )}
            </div>
            <Button variant="outline" size="sm" data-testid="drilldown-close"
                    onClick={() => setDrill(null)}>Close</Button>
          </section>
        )}
      </main>
    </div>
  );
}
